import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'
import type { WorkspaceUser } from '../../../queries'

import type { TeamContextEvents } from '../useTeamContext.events.schema'

type TeamContextHookMap = {
  [K in keyof TeamContextEvents]: (event: TeamContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered team context hook.
 */
type TeamContextUnsubscribe = () => void

interface TeamContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface UseTeamContextOnWorkspaceLoadedParams {
  hooks: Hookable<TeamContextHookMap>
  logger: TeamContextLoggerLike
  workspaceMembersQuery: Ref<UseLiveQueryReturn<WorkspaceUser> | undefined>
  createWorkspaceMembersQuery: (workspaceId: string) => UseLiveQueryReturn<WorkspaceUser> | undefined
}

/**
 * Internal handler: sets up workspace-scoped queries once the active workspace record is loaded.
 */
export default function useTeamContextOnWorkspaceLoaded({
  hooks,
  logger,
  workspaceMembersQuery,
  createWorkspaceMembersQuery,
}: UseTeamContextOnWorkspaceLoadedParams) {
  let activeWorkspaceId: string | null = null

  const unsubscribe: TeamContextUnsubscribe = hooks.hook('WorkspaceLoaded', ({ workspaceSlug, workspaceId }) => {
    if (!workspaceId) {
      activeWorkspaceId = null
      workspaceMembersQuery.value = undefined
      return
    }

    if (activeWorkspaceId === workspaceId) return

    activeWorkspaceId = workspaceId
    workspaceMembersQuery.value = createWorkspaceMembersQuery(workspaceId)
  })

  return unsubscribe
}
